import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { OutcomeCategory, ImpactLevel } from '../../types/outcomes';

interface CreateOutcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: {
    title: string;
    description: string;
    category: OutcomeCategory;
    impactLevel: ImpactLevel;
    targetDate?: string;
    relatedFrameworks: string[];
    impactMetrics: any[];
  }) => void;
}

export const CreateOutcomeModal: React.FC<CreateOutcomeModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<OutcomeCategory>('project');
  const [impactLevel, setImpactLevel] = useState<ImpactLevel>('medium');
  const [targetDate, setTargetDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) return;

    onCreate({
      title: title.trim(),
      description: description.trim(),
      category,
      impactLevel,
      targetDate: targetDate || undefined,
      relatedFrameworks: [],
      impactMetrics: [],
    });

    // Reset form
    setTitle('');
    setDescription('');
    setCategory('project');
    setImpactLevel('medium');
    setTargetDate('');
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-charcoal-900">Create New Outcome</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-charcoal-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-charcoal-900 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Implement DEGAP for Q1 roadmap prioritization"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-charcoal-900 mb-2">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what you want to achieve and why it matters..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-charcoal-900 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as OutcomeCategory)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="strategic">Strategic</option>
              <option value="operational">Operational</option>
              <option value="team">Team</option>
              <option value="personal">Personal</option>
              <option value="project">Project</option>
            </select>
          </div>

          {/* Impact Level */}
          <div>
            <label className="block text-sm font-medium text-charcoal-900 mb-2">
              Expected Impact
            </label>
            <select
              value={impactLevel}
              onChange={(e) => setImpactLevel(e.target.value as ImpactLevel)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="low">Low Impact</option>
              <option value="medium">Medium Impact</option>
              <option value="high">High Impact</option>
              <option value="transformational">Transformational Impact</option>
            </select>
          </div>

          {/* Target Date */}
          <div>
            <label className="block text-sm font-medium text-charcoal-900 mb-2">
              Target Completion Date
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-charcoal-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Create Outcome
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

